import { Icon } from '@starter/ui';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Row } from '../account/row.tsx';

/**
 * `/account/help` — the documents that govern an account, and the ones this
 * rebuild does not get to have.
 *
 * The original's shape, read out of its bundle: two groups — help and legal —
 * and six entries under them, each with a line saying what it covers (FAQ,
 * contact support, terms, privacy, cookies, refunds & billing).
 *
 * **Five of the six cannot honestly exist here.** Terms, a privacy policy, a
 * cookie policy, a refunds policy and a support desk are promises made by a
 * business to its customers; this is a rebuild with no customers, no payments
 * and nobody to answer an email. Publishing a plausible-looking Terms of
 * Service would be the one lie in this product that could actually hurt
 * somebody — so each entry is here, in its place, saying what it would cover
 * and that it is not in force. Recorded in `prd/DIVERGENCE.md`.
 *
 * The one that *is* real is the first: what this build is, what it does not
 * do, and where the difference from vendy is written down.
 */
interface Entry {
  label: string;
  hint: string;
  /** Where it goes, when it goes anywhere at all. */
  to?: '/deployment';
  testId: string;
}

const HELP: Entry[] = [
  {
    label: 'What this is',
    hint: 'A rebuild of vendy, up to the paywalls, with generation mocked.',
    testId: 'help-doc-about',
  },
  {
    label: 'What it will not do',
    hint: 'Take a payment, charge a card, or send anything to a real model.',
    testId: 'help-doc-limits',
  },
  {
    label: 'What is running',
    hint: 'The commit this deployment is serving, and when it went out.',
    to: '/deployment',
    testId: 'help-doc-deployment',
  },
];

const LEGAL: Entry[] = [
  {
    label: 'Terms of service',
    hint: 'Not in force. There is no agreement between this build and anybody.',
    testId: 'help-doc-terms',
  },
  {
    label: 'Privacy policy',
    hint: 'Not written. What is stored is an email, a name, and what you make here.',
    testId: 'help-doc-privacy',
  },
  {
    label: 'Refunds and billing',
    hint: 'Nothing to refund: no payment is taken anywhere in this product.',
    testId: 'help-doc-refunds',
  },
  {
    label: 'Contact support',
    hint: 'There is no support desk. Nobody is on the other end of an address.',
    testId: 'help-doc-support',
  },
];

function Entries({ entries }: { entries: Entry[] }) {
  return (
    <ul className="flex flex-col gap-3">
      {entries.map((entry) => (
        <li className="flex flex-col gap-0.5" data-testid={entry.testId} key={entry.label}>
          {entry.to ? (
            <Link
              className="inline-flex w-fit items-center gap-1.5 text-sm font-medium text-ink underline"
              to={entry.to}
            >
              {entry.label}
              <Icon name="arrow-right" size={14} />
            </Link>
          ) : (
            <span className="text-sm font-medium text-ink">{entry.label}</span>
          )}
          <span className="text-3xs leading-4 text-ink-muted">{entry.hint}</span>
        </li>
      ))}
    </ul>
  );
}

function HelpPage() {
  return (
    <div className="flex flex-col" data-testid="help-section">
      <Row label="Help" sub="What this build is, and what it is honestly not.">
        <Entries entries={HELP} />
      </Row>

      <Row
        label="Legal"
        sub="The documents a product like this has. None of them are in force here."
        testId="help-legal"
      >
        <Entries entries={LEGAL} />
      </Row>
    </div>
  );
}

export const Route = createFileRoute('/account/help')({
  component: HelpPage,
});
